import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Badge, Spinner, Alert, Modal, Form, Card } from 'react-bootstrap';
import { toUrl } from '../lib/config';

interface Slider {
  id: string;
  page: string;
  kicker: string;
  title: string;
  subtitle: string;
  subtitle2: string;
  titleTop: string;
  titleBottom: string;
  description: string;
  imageUrl: string;
  buttonLabel: string;
  buttonHref: string;
  overlayColor: string;
  overlayOpacity: number;
  accentColor: string;
  textColor: string;
  focus: string;
  align: string;
  theme: string;
  orderIndex: number;
  isActive: boolean;
  updatedAt: string;
}

// Image component with fallback
const ImgWithFallback: React.FC<{ src?: string | null; alt: string; height?: number }> = ({ src, alt, height = 50 }) => {
  const [hasError, setHasError] = React.useState(false);
  const [currentSrc, setCurrentSrc] = React.useState('');

  React.useEffect(() => {
    if (src && src.trim() !== '') {
      const baseIn = src;
      
      // Eğer /static/media/ gibi frontend asset path'i ise, doğrudan kullan
      if (baseIn.startsWith('/static/') || baseIn.startsWith('./') || baseIn.startsWith('../')) {
        setCurrentSrc(baseIn);
        setHasError(false);
        return;
      }
      
      // UUID extract et veya doğrudan API path kullan
      const m = baseIn.match(/([0-9a-fA-F-]{36})$/);
      const raw = m ? `/api/media/${m[1]}`  : baseIn;
      const pref = toUrl(raw);
      setCurrentSrc(pref);
      setHasError(false);
    }
  }, [src]);
  
  if (!src || src.trim() === '') {
    return (
      <div 
        style={{ 
          height, 
          width: height * 1.5,
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: 6,
          border: '1px solid #e5e7eb',
          fontSize: '10px',
          color: '#6c757d'
        }}
      >
        Resim Yok
      </div>
    );
  }

  if (hasError) {
    return (
      <div 
        style={{ 
          height, 
          width: height * 1.5,
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: 6,
          border: '1px solid #e5e7eb',
          fontSize: '10px',
          color: '#dc3545'
        }}
      >
        Yüklenemedi
      </div>
    );
  }
  
  return (
    <img
      src={currentSrc}
      alt={alt}
      style={{ height, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb' }}
      crossOrigin="anonymous"
      onError={() => setHasError(true)}
    />
  );
};

interface Media {
  id: string;
  name: string;
  url: string;
  filename: string;
  mime: string;
  sizeBytes: number;
  isActive: boolean;
}

const SliderYonetim: React.FC = () => {
  const [sliders, setSliders] = useState<Slider[]>([]);
  const [mediaList, setMediaList] = useState<Media[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSlider, setNewSlider] = useState({
    titleTop: '',
    titleBottom: '',
    subtitle: '',
    subtitle2: '',
    kicker: '',
    page: 'home',
    imageUrl: '',
    theme: 'default',
    orderIndex: 0
  });

  useEffect(() => {
    fetchSliders();
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      setLoadingMedia(true);
      const url = toUrl('/api/media/admin');
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        const mediaItems = data.data || data || [];
        setMediaList(mediaItems);
      } else {
        console.error('Media fetch failed:', response.status);
      }
    } catch (err) {
      console.error('Media yükleme hatası:', err);
    } finally {
      setLoadingMedia(false);
    }
  };

  const fetchSliders = async () => {
    try {
      setLoading(true);
      const url = toUrl('/api/slider/admin');
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        setSliders(data);
      } else {
        throw new Error('Slider listesi yüklenemedi');
      }
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSlider = async () => {
    try {
      setLoading(true);
      const url = toUrl('/api/slider/admin');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newSlider),
      });

      if (response.ok) {
        setSuccess('Slider başarıyla eklendi!');
        fetchSliders();
        setShowAddForm(false);
        setNewSlider({
          titleTop: '',
          titleBottom: '',
          subtitle: '',
          subtitle2: '',
          kicker: '',
          page: 'home',
          imageUrl: '',
          theme: 'default',
          orderIndex: 0
        });
      } else {
        throw new Error('Slider eklenemedi');
      }
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSlider = async (slider: Slider) => {
    try {
      setLoading(true);
      const url = toUrl(`/api/slider/admin/${slider.id}`);
      
      const payload = {
        ...slider,
        overlayOpacity: typeof slider.overlayOpacity === 'string' 
          ? parseFloat(slider.overlayOpacity) 
          : slider.overlayOpacity
      };
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setSuccess('Slider başarıyla güncellendi!');
        fetchSliders();
        setEditingId(null);
      } else {
        throw new Error('Slider güncellenemedi');
      }
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      setLoading(true);
      const url = toUrl(`/api/slider/admin/${deleteId}`);
      const response = await fetch(url, {
        method: 'DELETE',
      });

      if (response.ok) {
        setSuccess('Slider başarıyla silindi!');
        fetchSliders();
        setShowDeleteModal(false);
        setDeleteId(null);
      } else {
        throw new Error('Slider silinemedi');
      }
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (id: string, field: keyof Slider, value: any) => {
    setSliders(prev => prev.map(s => 
      s.id === id ? { ...s, [field]: value } : s
    ));
  };

  return (
    <Container fluid className="py-4">
      <h2 className="mb-4">Slider Yönetimi</h2>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

      <div className="mb-3">
        <Button variant="primary" onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? 'İptal' : '+ Yeni Slider Ekle'}
        </Button>
      </div>

      {showAddForm && (
        <Card className="mb-4">
          <Card.Body>
            <h5>Yeni Slider Ekle</h5>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Sayfa</Form.Label>
                <Form.Select 
                  value={newSlider.page} 
                  onChange={(e) => setNewSlider({...newSlider, page: e.target.value})}
                >
                  <option value="home">Ana Sayfa</option>
                  <option value="about">Hakkımızda</option>
                  <option value="doctors">Doktorlar</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Üst Başlık</Form.Label>
                <Form.Control 
                  type="text" 
                  value={newSlider.titleTop}
                  onChange={(e) => setNewSlider({...newSlider, titleTop: e.target.value})}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Alt Başlık</Form.Label>
                <Form.Control 
                  type="text" 
                  value={newSlider.titleBottom}
                  onChange={(e) => setNewSlider({...newSlider, titleBottom: e.target.value})}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Alt Yazı</Form.Label>
                <Form.Control 
                  as="textarea"
                  rows={2}
                  value={newSlider.subtitle}
                  onChange={(e) => setNewSlider({...newSlider, subtitle: e.target.value})}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Alt Yazı 2 (Opsiyonel)</Form.Label>
                <Form.Control 
                  as="textarea"
                  rows={2}
                  value={newSlider.subtitle2}
                  onChange={(e) => setNewSlider({...newSlider, subtitle2: e.target.value})}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Resim</Form.Label>
                <Form.Select 
                  value={newSlider.imageUrl} 
                  onChange={(e) => setNewSlider({...newSlider, imageUrl: e.target.value})}
                >
                  <option value="">Resim Seçiniz</option>
                  {mediaList.map((media) => (
                    <option key={media.id} value={media.url}>
                      {media.name || media.filename} ({media.id.substring(0, 8)}...)
                    </option>
                  ))}
                </Form.Select>
                {newSlider.imageUrl && (
                  <div className="mt-2">
                    <ImgWithFallback src={newSlider.imageUrl} alt="Preview" height={100} />
                  </div>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Tema</Form.Label>
                <Form.Select 
                  value={newSlider.theme} 
                  onChange={(e) => setNewSlider({...newSlider, theme: e.target.value})}
                >
                  <option value="default">Varsayılan</option>
                  <option value="alt">Alternatif</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Sıra</Form.Label>
                <Form.Control 
                  type="number" 
                  value={newSlider.orderIndex}
                  onChange={(e) => setNewSlider({...newSlider, orderIndex: parseInt(e.target.value)})}
                />
              </Form.Group>

              <Button variant="success" onClick={handleAddSlider} disabled={loading}>
                {loading ? <Spinner animation="border" size="sm" /> : 'Ekle'}
              </Button>
            </Form>
          </Card.Body>
        </Card>
      )}

      {loading && !showAddForm ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th style={{width: '80px'}}>Resim</th>
              <th>Sayfa</th>
              <th>Üst Başlık</th>
              <th>Alt Başlık</th>
              <th>Alt Yazı</th>
              <th>Tema</th>
              <th>Sıra</th>
              <th>Durum</th>
              <th style={{width: '200px'}}>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {sliders.map((slider) => (
              <tr key={slider.id}>
                <td>
                  {editingId === slider.id ? (
                    <div>
                      <Form.Select 
                        size="sm"
                        value={slider.imageUrl}
                        onChange={(e) => handleFieldChange(slider.id, 'imageUrl', e.target.value)}
                        className="mb-2"
                      >
                        <option value="">Resim Seçiniz</option>
                        {mediaList.map((media) => (
                          <option key={media.id} value={media.url}>
                            {media.name || media.filename}
                          </option>
                        ))}
                      </Form.Select>
                      {slider.imageUrl && (
                        <ImgWithFallback src={slider.imageUrl} alt={slider.titleTop} height={50} />
                      )}
                    </div>
                  ) : (
                    <ImgWithFallback src={slider.imageUrl} alt={slider.titleTop} height={50} />
                  )}
                </td>
                <td>
                  {editingId === slider.id ? (
                    <Form.Select 
                      size="sm"
                      value={slider.page}
                      onChange={(e) => handleFieldChange(slider.id, 'page', e.target.value)}
                    >
                      <option value="home">Ana Sayfa</option>
                      <option value="about">Hakkımızda</option>
                      <option value="doctors">Doktorlar</option>
                    </Form.Select>
                  ) : (
                    <Badge bg="secondary">{slider.page}</Badge>
                  )}
                </td>
                <td>
                  {editingId === slider.id ? (
                    <Form.Control 
                      size="sm"
                      type="text"
                      value={slider.titleTop}
                      onChange={(e) => handleFieldChange(slider.id, 'titleTop', e.target.value)}
                    />
                  ) : (
                    slider.titleTop
                  )}
                </td>
                <td>
                  {editingId === slider.id ? (
                    <Form.Control 
                      size="sm"
                      type="text"
                      value={slider.titleBottom}
                      onChange={(e) => handleFieldChange(slider.id, 'titleBottom', e.target.value)}
                    />
                  ) : (
                    slider.titleBottom
                  )}
                </td>
                <td>
                  {editingId === slider.id ? (
                    <Form.Control 
                      size="sm"
                      as="textarea"
                      rows={2}
                      value={slider.subtitle}
                      onChange={(e) => handleFieldChange(slider.id, 'subtitle', e.target.value)}
                    />
                  ) : (
                    <div style={{maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                      {slider.subtitle}
                    </div>
                  )}
                </td>
                <td>
                  {editingId === slider.id ? (
                    <Form.Select 
                      size="sm"
                      value={slider.theme}
                      onChange={(e) => handleFieldChange(slider.id, 'theme', e.target.value)}
                    >
                      <option value="default">Varsayılan</option>
                      <option value="alt">Alternatif</option>
                    </Form.Select>
                  ) : (
                    <Badge bg={slider.theme === 'alt' ? 'info' : 'primary'}>{slider.theme}</Badge>
                  )}
                </td>
                <td>
                  {editingId === slider.id ? (
                    <Form.Control 
                      size="sm"
                      type="number"
                      value={slider.orderIndex}
                      onChange={(e) => handleFieldChange(slider.id, 'orderIndex', parseInt(e.target.value))}
                      style={{width: '70px'}}
                    />
                  ) : (
                    slider.orderIndex
                  )}
                </td>
                <td>
                  {editingId === slider.id ? (
                    <Form.Check 
                      type="switch"
                      checked={slider.isActive}
                      onChange={(e) => handleFieldChange(slider.id, 'isActive', e.target.checked)}
                    />
                  ) : (
                    <Badge bg={slider.isActive ? 'success' : 'danger'}>
                      {slider.isActive ? 'Aktif' : 'Pasif'}
                    </Badge>
                  )}
                </td>
                <td>
                  {editingId === slider.id ? (
                    <>
                      <Button 
                        size="sm" 
                        variant="success" 
                        onClick={() => handleUpdateSlider(slider)}
                        disabled={loading}
                        className="me-2"
                      >
                        Kaydet
                      </Button>
                      <Button 
                        size="sm" 
                        variant="secondary" 
                        onClick={() => {
                          setEditingId(null);
                          fetchSliders();
                        }}
                      >
                        İptal
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button 
                        size="sm" 
                        variant="warning" 
                        onClick={() => setEditingId(slider.id)}
                        className="me-2"
                      >
                        Düzenle
                      </Button>
                      <Button 
                        size="sm" 
                        variant="danger" 
                        onClick={() => {
                          setDeleteId(slider.id);
                          setShowDeleteModal(true);
                        }}
                      >
                        Sil
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Slider Sil</Modal.Title>
        </Modal.Header>
        <Modal.Body>Bu slider'ı silmek istediğinizden emin misiniz?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            İptal
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : 'Sil'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SliderYonetim;
